import type { LedgerEvent } from '@talos/kyoko';
import { useMemo } from 'react';

type PinnedRowData = Pick<LedgerEvent, 'ResultingAmount' | 'Asset' | 'amountAssetWithFallback'> & {
  rowID: string;
  Type: 'Interval Start Balance' | 'Interval End Balance';
};
/**
 * Creates pinned row data for displaying interval start/end balance in the ledger events blotter.
 * Returns a single-row array with balance information when in reports modal and the toggle is on with valid event data,
 * otherwise returns undefined.
 *
 * @param inReportsModal - Whether the blotter is displayed in the reports modal context. Pinned rows are only shown in reports.
 * @param showIntervalBalanceRows - Whether to show pinned rows for the interval start/end balances
 * @param event - The ledger event data (firstEvent for start balance, lastEvent for end balance)
 * @param type - Whether this is the 'start' or 'end' interval balance row
 */
export function usePinnedRowData(
  showIntervalBalanceRows: boolean | undefined,
  event: LedgerEvent | undefined,
  type: 'start' | 'end'
): PinnedRowData[] | undefined {
  return useMemo(() => {
    if (!showIntervalBalanceRows || !event) {
      return undefined;
    }

    const isStart = type === 'start';
    return [
      {
        rowID: `pinned-${type}-summary`,
        // Balance Value
        ResultingAmount: isStart ? event.startingAmount || '0' : event.ResultingAmount || '0',
        // Talos Ledger Event
        Type: isStart ? 'Interval Start Balance' : 'Interval End Balance',
        Asset: event.Asset,
        amountAssetWithFallback: event.AmountAsset || event.Asset,
      },
    ];
  }, [showIntervalBalanceRows, event, type]);
}
